import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import { School as SchoolIcon } from '@mui/icons-material';
import Button from './UIKit/Button';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const DeleteLessonDialog = ({
  open,
  onClose,
  onConfirm,
  lesson,
  dependencies = null, // { courses: [], modules: [] }
  loading = false,
}) => {
  const { t } = useTranslation(['lessons', 'common']);

  const hasDependencies = dependencies && (
    (dependencies.courses && dependencies.courses.length > 0) ||
    (dependencies.modules && dependencies.modules.length > 0)
  );

  const handleConfirm = () => {
    if (hasDependencies) {
      onClose();
      return;
    }
    onConfirm();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '12px',
          p: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <SchoolIcon sx={{ color: 'warning.main', fontSize: 28 }} />
          <Typography variant="h6" component="span" fontWeight={600}>
            {t('common:confirmDelete')}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {hasDependencies ? (
          <Box>
            <Alert
              severity="warning"
              sx={{
                mb: 3,
                borderRadius: '8px',
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                {t('lessons:library.deleteDialog.cannotDeleteWarning')}
              </Typography>
            </Alert>

            {/* Courses */}
            {dependencies.courses && dependencies.courses.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
                  {t('lessons:library.deleteDialog.coursesUsingLesson')}
                </Typography>
                <Grid container spacing={2}>
                  {dependencies.courses.map((course) => (
                    <Grid item xs={12} key={course.id || course._id}>
                      <Link
                        to={`/courses/${course.slug}`}
                        style={{ textDecoration: 'none' }}
                        onClick={onClose}
                      >
                        <Card
                          variant="outlined"
                          sx={{
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: 1,
                              borderColor: 'primary.main',
                            }
                          }}
                        >
                          <CardContent sx={{ py: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                              {course.title}
                            </Typography>
                            {course.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {course.description}
                              </Typography>
                            )}
                            {course.instructor && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {t('common:instructor')}: {course.instructor.userMeta?.fullName || t('common:unknown')}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Link>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Modules */}
            {dependencies.modules && dependencies.modules.length > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
                  {t('lessons:library.deleteDialog.modulesContainingLesson')}
                </Typography>
                <Grid container spacing={2}>
                  {dependencies.modules.map((module) => (
                    <Grid item xs={12} key={module.id || module._id}>
                      <Link
                        to={`/modules/${module.slug}`}
                        style={{ textDecoration: 'none' }}
                        onClick={onClose}
                      >
                        <Card
                          variant="outlined"
                          sx={{
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: 1,
                              borderColor: 'primary.main',
                            }
                          }}
                        >
                          <CardContent sx={{ py: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                              {module.title}
                            </Typography>
                            {module.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {module.description}
                              </Typography>
                            )}
                            {module.author && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {t('common:author')}: {module.author.userMeta?.fullName || t('common:unknown')}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Link>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            <Typography variant="body1" color="text.secondary">
              {t('lessons:library.deleteConfirmation', {
                title: lesson?.title,
              })}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{ borderRadius: '8px', minWidth: 100 }}
        >
          {hasDependencies ? t('common:actions.close') : t('common:actions.cancel')}
        </Button>

        {!hasDependencies && (
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirm}
            disabled={loading}
            sx={{ borderRadius: '8px', minWidth: 100 }}
          >
            {loading ? t('common:actions.deleting') : t('common:actions.delete')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DeleteLessonDialog;
